/**
 * Locale handling. Content ("reading") locales and UI locales are independent settings.
 * Codes are BCP-47. The supported lists are the pilot set; the type stays `string` so any
 * BCP-47 locale can be added without schema changes.
 */

export const READING_LOCALES = ['sv-SE', 'zh-Hans', 'zh-Hant', 'en'] as const;
export const UI_LOCALES = ['sv-SE', 'zh-Hans', 'en'] as const;

export type ReadingLocale = (typeof READING_LOCALES)[number];
export type UiLocale = (typeof UI_LOCALES)[number];

export const LOCALE_NAMES: Record<string, { native: string; en: string }> = {
  'sv-SE': { native: 'Svenska', en: 'Swedish' },
  'zh-Hans': { native: '简体中文', en: 'Chinese (Simplified)' },
  'zh-Hant': { native: '繁體中文', en: 'Chinese (Traditional)' },
  en: { native: 'English', en: 'English' },
};

export function isReadingLocale(v: unknown): v is ReadingLocale {
  return typeof v === 'string' && (READING_LOCALES as readonly string[]).includes(v);
}

export function isUiLocale(v: unknown): v is UiLocale {
  return typeof v === 'string' && (UI_LOCALES as readonly string[]).includes(v);
}

/** Primary language subtag, e.g. "sv-SE" -> "sv", "zh-Hans" -> "zh". */
export function primaryLanguage(locale: string): string {
  return locale.split('-')[0]!.toLowerCase();
}

/**
 * Whether a reader in `reader` locale can read text in `source` locale without translation.
 * zh-Hans and zh-Hant are deliberately treated as different (script conversion is a translation step).
 */
export function sameReadingLanguage(source: string, reader: string): boolean {
  if (source === reader) return true;
  const a = primaryLanguage(source);
  const b = primaryLanguage(reader);
  if (a === 'zh' || b === 'zh') return false;
  return a === b;
}

/** Best-effort mapping from a browser language list to a supported UI locale. */
export function pickUiLocale(browserLanguages: readonly string[]): UiLocale {
  for (const raw of browserLanguages) {
    const l = raw.toLowerCase();
    if (l.startsWith('sv')) return 'sv-SE';
    if (l.startsWith('zh')) return 'zh-Hans';
    if (l.startsWith('en')) return 'en';
  }
  return 'en';
}
